import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProgressiveImage from '../common/ProgressiveImage';

/**
 * Coursera-inspired compact discovery card — thumbnail, provider row, 2-line title, type label.
 */
function ShortCard({
  to,
  onClick,
  image,
  imageFallback,
  badges = [],
  providerLogo,
  providerName,
  title,
  typeLabel,
  footer,
  className = '',
  index = 0,
  selected = false,
  children,
}) {
  const inner = (
    <>
      <div className="ux-short-card-media">
        {image ? (
          <ProgressiveImage src={image} alt={title || ''} className="ux-short-card-img" aspectRatio="16/9" loading="lazy" />
        ) : (
          <div className="ux-short-card-img-fallback">{imageFallback}</div>
        )}
        {badges.length > 0 && (
          <div className="ux-short-card-badges">
            {badges.map((b) => (
              <span key={b.label} className={`ux-short-badge ux-short-badge-${b.variant || 'default'}`}>
                {b.label}
              </span>
            ))}
          </div>
        )}
        {children}
      </div>
      <div className="ux-short-card-body">
        {(providerLogo || providerName) && (
          <div className="ux-short-card-provider">
            {providerLogo && <span className="ux-short-card-provider-logo">{providerLogo}</span>}
            {providerName && <span className="ux-short-card-provider-name">{providerName}</span>}
          </div>
        )}
        <h3 className="ux-short-card-title">{title}</h3>
        {typeLabel && <div className="ux-short-card-type">{typeLabel}</div>}
        {footer}
      </div>
    </>
  );

  const cardClass = `ux-short-card ${selected ? 'is-selected' : ''} ${className}`.trim();

  if (to) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.24) }}
      >
        <Link to={to} className={cardClass}>{inner}</Link>
      </motion.div>
    );
  }

  const Tag = onClick ? 'button' : 'div';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.24) }}
    >
      <Tag type={onClick ? 'button' : undefined} className={cardClass} onClick={onClick}>
        {inner}
      </Tag>
    </motion.div>
  );
}

export function ShortCardGrid({ children, className = '' }) {
  return <div className={`ux-short-grid ${className}`.trim()}>{children}</div>;
}

export function ListSection({ title, description, action, children }) {
  return (
    <section className="ux-list-section">
      {(title || action) && (
        <div className="ux-list-section-head">
          <div>
            {title && <h2 className="ux-list-section-title">{title}</h2>}
            {description && <p className="ux-list-section-desc">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export default ShortCard;
